import { useEffect, useState } from "react";
import { Calendar, Copy, ArrowsClockwise, AppleLogo, MicrosoftOutlookLogo, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { Button } from "../ui/button";

/**
 * AppleCalendarCard — Pro-only "subscribe to my schedule" feed.
 *
 * The owner generates a token-based URL once. They (or anyone they share
 * it with) paste it into Apple Calendar, Outlook, Google Calendar, or any
 * iCal-compatible app and the schedule auto-syncs every ~30 min. The
 * token is the auth — regenerating it instantly revokes prior subscribers.
 */
export default function AppleCalendarCard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState(null); // { token, url, truck_name, created_at } | null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let on = true;
    api.get("/ical/me/token")
      .then((r) => on && setFeed(r.data || null))
      .catch(() => on && setFeed(null))
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  if (!user || user.role !== "owner") return null;

  const isPro = !!user.is_pro;

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ical/me/token");
      setFeed(r.data);
      toast.success("Calendar feed URL ready — paste it into any calendar app.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate feed URL");
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    if (!window.confirm("Generate a new URL? The current URL will stop working for everyone subscribed to it.")) return;
    await generate();
  };

  const revoke = async () => {
    if (!window.confirm("Disable the calendar feed entirely? All current subscribers will stop syncing.")) return;
    setBusy(true);
    try {
      await api.delete("/ical/me/token");
      setFeed(null);
      toast.success("Calendar feed disabled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disable feed");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    if (!feed?.url) return;
    try {
      await navigator.clipboard.writeText(feed.url);
      toast.success("Feed URL copied to clipboard.");
    } catch {
      toast.error("Copy failed — long-press the URL to copy manually.");
    }
  };

  const webcalUrl = feed?.url ? feed.url.replace(/^https?:/, "webcal:") : null;

  return (
    <div
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      data-testid="apple-calendar-card"
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2.5">
          <Calendar weight="duotone" className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold text-lg">Subscribe-able calendar feed</h2>
        </div>
        {!isPro && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded-full bg-primary/10 text-primary px-2 py-0.5 font-bold shrink-0">
            <Crown weight="fill" className="h-2.5 w-2.5" />
            Pro
          </span>
        )}
      </div>
      <p className="text-[13px] text-muted-foreground leading-relaxed mb-4">
        Share a personal URL that any calendar app can subscribe to — Apple Calendar, Outlook, Google Calendar, Fantastical. Your stops show up on your phone&apos;s built-in calendar, your customers&apos; calendars if they subscribe, even your accountant&apos;s. Auto-refreshes every 30 minutes.
      </p>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : !isPro ? (
        <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3 text-sm text-foreground">
          The calendar subscription feed is a Pro perk. Upgrade in your settings to unlock.
        </div>
      ) : !feed ? (
        <Button
          onClick={generate}
          disabled={busy}
          className="font-semibold"
          data-testid="ical-generate-btn"
        >
          {busy ? "Generating…" : "Generate my feed URL"}
        </Button>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-background px-3 py-2.5 flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate text-[12px] text-foreground font-mono" data-testid="ical-feed-url">
              {feed.url}
            </code>
            <button
              type="button"
              onClick={copyUrl}
              className="shrink-0 rounded-md p-1.5 hover:bg-muted/60 text-muted-foreground hover:text-foreground"
              data-testid="ical-copy-btn"
              aria-label="Copy URL"
              title="Copy URL"
            >
              <Copy weight="bold" className="h-4 w-4" />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            <a
              href={webcalUrl}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-card hover:bg-muted/40 px-3 py-2 text-[12px] font-semibold transition"
              data-testid="ical-apple-add-btn"
            >
              <AppleLogo weight="fill" className="h-3.5 w-3.5" />
              Open in Apple Calendar
            </a>
            <a
              href={feed.url}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-card hover:bg-muted/40 px-3 py-2 text-[12px] font-semibold transition"
              target="_blank"
              rel="noreferrer"
              data-testid="ical-download-btn"
            >
              <MicrosoftOutlookLogo weight="fill" className="h-3.5 w-3.5" />
              Outlook / Other
            </a>
          </div>

          <details className="text-[13px] text-muted-foreground" data-testid="ical-instructions">
            <summary className="cursor-pointer font-semibold text-foreground hover:text-primary">
              How to subscribe in each app
            </summary>
            <div className="mt-2 space-y-2 leading-relaxed">
              <div><strong>Apple Calendar (Mac):</strong> File → New Calendar Subscription → paste URL.</div>
              <div><strong>Apple Calendar (iPhone):</strong> Tap the &ldquo;Open in Apple Calendar&rdquo; button above — iOS handles the rest.</div>
              <div><strong>Outlook (desktop / web):</strong> Add Calendar → Subscribe from web → paste URL.</div>
              <div><strong>Google Calendar:</strong> Settings → Add calendar → From URL → paste URL.</div>
            </div>
          </details>

          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={rotate}
              disabled={busy}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-60"
              data-testid="ical-rotate-btn"
            >
              <ArrowsClockwise weight="bold" className="h-3 w-3" />
              Rotate URL
            </button>
            <span className="text-muted-foreground/50 text-[11px]">·</span>
            <button
              type="button"
              onClick={revoke}
              disabled={busy}
              className="text-[11px] font-semibold text-muted-foreground hover:text-rose-600 disabled:opacity-60"
              data-testid="ical-revoke-btn"
            >
              Disable feed
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
